import type { ColumnDef } from '@tanstack/react-table';
import type { MovimientoFondo } from '@condo/contracts';
import { DataTable } from '@/components/common/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatClp } from '@/lib/format/currency';
import { formatShortDate } from '@/lib/format/date';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CATEGORIA_LABELS: Record<string, string> = {
  auto_asignacion: 'Auto-asignacion',
  aporte_extraordinario: 'Aporte extraordinario',
  gasto_autorizado: 'Gasto autorizado',
  reversion_anulacion: 'Reversion anulacion',
};

const columns: ColumnDef<MovimientoFondo>[] = [
  {
    accessorKey: 'creadoEn',
    header: 'Fecha',
    cell: ({ row }) => (
      <span className="text-xs text-[var(--color-ink-muted)]">
        {formatShortDate(row.original.creadoEn)}
      </span>
    ),
  },
  {
    accessorKey: 'tipo',
    header: 'Tipo',
    cell: ({ row }) => {
      const tipo = row.original.tipo;
      return (
        <Badge variant={tipo === 'ingreso' ? 'success' : 'danger'}>
          {tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'categoria',
    header: 'Categoria',
    cell: ({ row }) => (
      <span className="text-sm">
        {CATEGORIA_LABELS[row.original.categoria] ?? row.original.categoria}
      </span>
    ),
  },
  {
    accessorKey: 'descripcion',
    header: 'Descripcion',
    cell: ({ row }) => (
      <span className="max-w-[200px] truncate text-sm">
        {row.original.descripcion}
      </span>
    ),
  },
  {
    accessorKey: 'monto',
    header: 'Monto',
    cell: ({ row }) => {
      const { tipo, monto } = row.original;
      return (
        <span
          className={`font-display tabular font-semibold ${
            tipo === 'ingreso'
              ? 'text-[var(--color-moss)]'
              : 'text-[var(--color-danger)]'
          }`}
        >
          {tipo === 'ingreso' ? '+' : '-'}{formatClp(monto)}
        </span>
      );
    },
  },
  {
    accessorKey: 'saldoNuevo',
    header: 'Saldo',
    cell: ({ row }) => (
      <span className="font-display tabular font-semibold">
        {formatClp(row.original.saldoNuevo)}
      </span>
    ),
  },
];

interface MovimientosTableProps {
  data: MovimientoFondo[];
  isLoading?: boolean;
  cursor?: string | null;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  hasPrevPage?: boolean;
}

export function MovimientosTable({
  data,
  isLoading,
  cursor,
  onNextPage,
  onPrevPage,
  hasPrevPage,
}: MovimientosTableProps) {
  return (
    <div className="space-y-4">
      <DataTable columns={columns} data={data} isLoading={isLoading} />

      {(hasPrevPage || cursor) && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onPrevPage}
            disabled={!hasPrevPage}
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNextPage}
            disabled={!cursor}
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
